import {Map as ImmutableMap} from 'immutable';
import _, {Dictionary} from 'lodash';
import {LRUCache} from 'lru-cache';
import {deepClone} from '@packages/systems/core/utils/JsUtils';
import {
  PSEUDOS_ORDER,
  COMBINATORS,
  REDIRECTED_PSEUDOS,
} from '@packages/systems/core/constants/Selectors';
import {
  getUpstreamPseudos,
  buildSelectorToken,
  getSelectorScore,
  getType,
  getTagName,
  getTokenLabel,
  VariantDataMap,
  VariantStateKey,
} from '@packages/systems/style-legacy/selectors';
import {
  parseVariantKey,
  createVariantKey,
} from '@packages/systems/style-legacy/variants';
import {
  cssToPropertyList,
  cssToPropertyMap,
  isCssVariableProperty,
} from '@packages/systems/core/utils/CssStringUtils';
import {getPseudoStateKey} from '@packages/systems/core/utils/StyleTypes';

import {CSS_PROPERTY_DETAILS} from '@packages/shared/LegacyStyleSystem/utils/CssPropertyUtils';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {constant, emptyArray, identity} from '@packages/utilities/fp/utils';
import {
  calculateAffectedForElement,
  calculateAffectedForAncestorPath,
} from '@packages/shared/utils/AffectedNodesUtils';
import {getSuggestionApplyResult} from './StyleBlockUtils';

import {generateNewClassName} from '../../utils/ClassNameGenerationUtils';
import {getElementLabel} from '@packages/utilities/platform';
import {Json} from '@packages/utilities/json';
import {
  selectCurrentComponent,
  getLiveComponentMap,
  getPluginsFromState,
} from '@packages/utilities/platform-selectors';
import {titleize} from '@packages/utilities/string-utils';

import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';

import {
  hyphenateStyleName,
  NO_PSEUDO,
  TAG,
  CURRENT,
} from '@packages/systems/core/utils/StyleUtils';

import type {
  PseudoStateKey,
  CustomNodeStateKey,
} from '@packages/systems/core/utils/StyleTypes';
import {
  type Selector,
  type SelectorChunk,
  type StyleRule,
  type StyleRuleType,
  STYLE_BLOCK_STYLE_RULE,
  LOCAL_STYLE_RULE,
  DEFAULT_STYLE_RULE,
} from '@packages/systems/style-legacy/rules';

import type {
  StyleBlock,
  StyleBlockId,
} from '@packages/systems/style-legacy/records';
import type {SuggestionType} from './StyleRuleUtilsTypes';
import {
  getTokensForVariants,
  createStyleRuleGuid,
} from '@packages/shared/LegacyStyleSystem/utils/StyleRuleUtils';
import {
  type NormalizedAtomConfig,
  Component,
  Plugins,
  Expressions,
  matchExpression,
  ExpressionPath,
} from '@packages/systems/wfdl';

import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';
import type {
  BreakpointID,
  BreakpointsMap,
  DefaultBaseStyleConfig,
} from '@packages/systems/style/types';
import {Expression, type ExpressionElement} from '@packages/systems/wfdl-ast';
import {getIsBranchMode} from '@packages/systems/designer-selectors';
import {
  WebflowCssProperties,
  convertStyleValueToString,
} from '@packages/systems/core/utils/WebflowCssSubset';
import {generateVariantDataMap} from '@packages/systems/component-style-variants';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {
  hasCapability,
  LibrariesCapabilitiesMap,
  removeHiddenStyleBlockTagStyling,
} from '@packages/systems/libraries';
import {isElementStyle} from '@packages/systems/style/utils';
import {AutoCreatePreference} from './ElementStylesUtils';
import {getStylesFromStyleBlock} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
const {isPluginComponentInst, getPluginComponent} = Plugins;

export {getTokensForVariants, createStyleRuleGuid};

// JS / POJO version of StyleBlockVariant in StyleBlockRecord.js
type Variant = {
  styleLess: string;
  selector: Selector;
};

const isRuleEditable = (rule: StyleRule): boolean =>
  rule.type === STYLE_BLOCK_STYLE_RULE && !rule.foreign;

// Not a UI selector:
// eslint-disable-next-line webflow/selector-memoization
export const selectEditableRules = (
  rules: Array<StyleRule>
): Array<StyleRule> => rules.filter(isRuleEditable);

export const createStyleRuleGuidFromStyleRule = (rule?: StyleRule | null) =>
  rule && rule.styleBlock
    ? createStyleRuleGuid(rule.styleBlock, createVariantKey(rule))
    : null;

/**
 * Creates a new StyleRule (POJO) object.
 * @param  {String}  options.styleBlock   The parent StyleBlock's ID.
 * @param  {String}  options.mediaQuery   The guid/key of the media query this rule is in, e.g. 'main', 'medium', etc.
 * @param  {STATES}  options.customNodeState The special Webflow derived state (e.g. w--current for links), if any.
 * @param  {PSEUDOS} options.pseudoState  The pseudo state of this rule, e.g. 'hover', 'active'
 * @param  {String}  options.styleLess    The style text associated with this selector.
 * @param  {Object}  options.selector     Selector data (text, score, chunks, tokens, etc)
 * @param  {Boolean} options.inherited    If true, this style rule contributes inherited styles (adds type-related styles), but is not attached to the node directly.
 * @param  {Boolean} options.foreign      If true, this style rule contributes to the current element styles via class selector(s), but is not attached to the node directly.
 * @param  {Boolean} options.mint         If true, this rule was just created automatically by getLinkedRules(), and is not in the style sheet yet.
 * @return {Object}                       e.g. { guid: 'block-1:medium_hover', mediaQuery: 'medium', etc }
 */
function createStyleRule(options: {
  mediaQuery: BreakpointID;
  customNodeState: CustomNodeStateKey | null | undefined;
  pseudoState: PseudoStateKey | null | undefined;
  variantState: VariantStateKey | null | undefined;
  styleBlock: string;
  styleLess?: string;
  selector: Selector;
  inheritedDistance?: number;
  foreign?: boolean;
  mint?: boolean;
  type?: StyleRuleType;
  styles?: Record<any, any>;
  origin: null | Qualified<StyleBlockId>;
}): StyleRule {
  const {
    styleBlock,
    mediaQuery,
    customNodeState = null,
    pseudoState = null,
    variantState = null,
    styleLess = '',
    selector,
    inheritedDistance = 0,
    foreign = false,
    mint = false,
    type = STYLE_BLOCK_STYLE_RULE,
    styles = {},
    origin,
  } = options;
  if (
    type === STYLE_BLOCK_STYLE_RULE &&
    (!styleBlock || typeof styleBlock !== 'string' || styleBlock.trim() === '')
  ) {
    throw new Error(
      `Invalid block: ${
        styleBlock
          ? styleBlock
          : 'No StyleBlock Provided for STYLE_BLOCK_STYLE_RULE'
      }`
    );
  }

  if (!mediaQuery || !_.isString(mediaQuery)) {
    throw new Error('Invalid mediaQuery: ' + mediaQuery);
  }

  return {
    styleBlock,
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
    styleLess,
    score: selector ? selector.score : 0,
    chunks:
      selector && selector.chunks
        ? selector.chunks.map((chunk) => {
            return {tokens: chunk.tokens, combinator: null};
          })
        : null,
    get inherited() {
      return this.inheritedDistance > 0;
    },
    inheritedDistance,
    foreign: Boolean(foreign),
    mint: Boolean(mint),
    type,
    styles,
    origin,
  };
}

/**
 * Derives the identifying guid for the given style rule.
 * @param   {StyleRule} styleRule   The rule to get the guid from.
 * @return  {String}                The rule's GUID, e.g. "block_id:medium_hover"
 */
export function getGuid({
  styleBlock,
  mediaQuery,
  customNodeState,
  pseudoState,
  variantState,
}: StyleRule) {
  // XXX test this
  if (!styleBlock) return null;
  const variantKey = createVariantKey({
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
  });

  return createStyleRuleGuid(styleBlock, variantKey);
}

/**
 * Sorts a list of applied rules by inherited/direct -> specificity -> media query -> index in style tree.
 * @param  {StyleRule[]}  styleRules          A list of StyleRules (as build by createStyleRule) to sort in-place.
 * @param  {String[]}     options.queryOrder  The global order of MediaQuery guid/keys.
 * @param  {Object}       options.blockOrder  A map showing the index of each StyleBlock guid in the topologically sorted style tree.
 * @return {StyleRule[]}                      A sorted (shallow) copy of the same array.
 */
function sortStyleRules(
  styleRules: Array<StyleRule>,
  options: {
    queryOrder: Array<BreakpointID>;
    blockOrder: {
      [key: string]: number;
    };
    styleBlocks: StyleBlockStateType['styleBlocks'];
  }
) {
  const {queryOrder = [], blockOrder = {}, styleBlocks} = options || {};
  const target = [...styleRules];

  function isElementStyleRule(rule: StyleRule): boolean {
    if (rule.styleBlock == null) return false;
    return isElementStyle(styleBlocks.get(rule.styleBlock));
  }

  target.sort((rule1: StyleRule, rule2: StyleRule) => {
    // Sort element styles after everything else:
    if (!isElementStyleRule(rule1) && isElementStyleRule(rule2)) return -1;
    if (isElementStyleRule(rule1) && !isElementStyleRule(rule2)) return 1;

    // If both rules are inherited, the node closest to the target node contains its inherited value, if they are of the same distance
    // we should resort to block order.
    if (
      rule1.inherited &&
      rule2.inherited &&
      rule1.inheritedDistance !== rule2.inheritedDistance
    ) {
      return rule2.inheritedDistance - rule1.inheritedDistance;
    }

    // Upstream rules always go before direct ones, regardless of specificity, because CSS always grants preference to direct selectors
    // over inherited rules, even when !important is used on those upstream rules
    if (rule1.inherited !== rule2.inherited) {
      return rule1.inherited ? -1 : 1;
    }

    // Then sort by specificity since that determines CSS application order
    if (rule1.score !== rule2.score) {
      return rule1.score - rule2.score;
    }

    // If the score collides, sort by the media query's index in the global sheet
    const queryIndex1 = queryOrder.indexOf(rule1.mediaQuery);
    const queryIndex2 = queryOrder.indexOf(rule2.mediaQuery);
    if (queryIndex1 !== queryIndex2) {
      return queryIndex1 - queryIndex2;
    }

    // Then sort by the desired pseudo order (e.g. :hover should be before :active)
    // @ts-expect-error - TS2345 - Argument of type '"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within" | null' is not assignable to parameter of type '"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within" | undefined'.
    const pseudoIndex1 = PSEUDOS_ORDER.indexOf(rule1.pseudoState);
    // @ts-expect-error - TS2345 - Argument of type '"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within" | null' is not assignable to parameter of type '"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within" | undefined'.
    const pseudoIndex2 = PSEUDOS_ORDER.indexOf(rule2.pseudoState);
    if (
      rule1.pseudoState &&
      rule2.pseudoState &&
      pseudoIndex1 !== pseudoIndex2
    ) {
      return pseudoIndex1 - pseudoIndex2;
    }

    // If both are atom rules, trust the atom rule provider.
    if (
      rule1.type === DEFAULT_STYLE_RULE &&
      rule2.type === DEFAULT_STYLE_RULE
    ) {
      return 0;
    }

    // Order by the StyleBlock tree topological order if both are styleblocks
    if (rule1.styleBlock && rule2.styleBlock) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
      return blockOrder[rule1.styleBlock] - blockOrder[rule2.styleBlock];
    }

    if (rule1.type !== rule2.type) {
      // If one rule is an atom type and the other is a style block the atom should come first.
      if (rule1.type === DEFAULT_STYLE_RULE) {
        return -1;
        // If one rule is a style block rule and one is a local style rule the style block should come first.
      } else if (
        rule1.type === STYLE_BLOCK_STYLE_RULE &&
        rule2.type === LOCAL_STYLE_RULE
      ) {
        return -1;
      } else {
        return 1;
      }
    }

    return 0;
  });

  return target;
}

/**
 * Collects nested style rule information from the main rule and variants.
 * @param  {StyleBlockRecord} styleBlock
 * @param  {String[]}         options.breakpointIds    The list of media query keys to filter by (e.g. 'main', 'medium')
 * @param  {String[]}         options.customNodeStates   The list of custom node state names to filter by (e.g. 'current', 'open')
 * @param  {String[]}         options.pseudoStates    The list of pseudo states to filter by (e.g. 'hover', 'active')
 * @return {Object[]}                                 A list of style rule data embedded in the block.
 */
function pluckStyleRules_old(
  styleBlock: StyleBlock,
  options?: {
    breakpointIds: Array<BreakpointID>;
    customNodeStates: Array<CustomNodeStateKey>;
    pseudoStates: Array<PseudoStateKey>;
    variantStates: Array<VariantStateKey>;
  }
) {
  const {
    breakpointIds = [],
    customNodeStates = [],
    pseudoStates = [],
    variantStates = [],
  } = options || {};
  if (!styleBlock) {
    return [];
  }

  if (!styleBlock.get('selector')) {
    throw new Error('StyleBlock must be decorated with selector!');
  }

  // @ts-expect-error - TS2322 - Type 'Map<string, Variant>' is not assignable to type 'Map<BreakpointID, Variant>'.
  const rulesMap: Map<BreakpointID, Variant> = new Map(
    _.toPairs({
      main: {
        styleLess: styleBlock.get('styleLess'),
        // @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2339 - Property 'toJS' does not exist on type 'Selector'.
        selector: styleBlock.get('selector').toJS(),
      },
      ...styleBlock.get('variants').toJS(),
    })
  );

  const styleRules: Array<StyleRule> = [];
  rulesMap.forEach((variant, variantKey) => {
    const {mediaQuery, customNodeState, pseudoState, variantState} =
      parseVariantKey(variantKey);

    // Don't include this rule if it belongs to a media query that wasn't requested
    if (breakpointIds.indexOf(mediaQuery) < 0) {
      return;
    }

    // If there's a custom node state (e.g. w--current, w--open) set, but it's not in the list of requested states, don't include the rule
    if (customNodeState && customNodeStates.indexOf(customNodeState) < 0) {
      return;
    }

    // If there's a pseudo variant but it's not in the requested set, ignore it
    if (pseudoState && pseudoStates.indexOf(pseudoState) < 0) {
      return;
    }

    if (variantState && variantStates.indexOf(variantState) < 0) {
      return;
    }

    // If all filters have passed, pluck the rule data
    styleRules.push(
      createStyleRule({
        styleBlock: styleBlock.get('_id'),
        mediaQuery,
        customNodeState,
        pseudoState,
        variantState,
        styleLess: variant.styleLess,
        selector: variant.selector,
        origin: styleBlock.origin,
      })
    );
  });

  return styleRules;
}

/**
 * Collects nested style rule information from the main rule and variants.
 * @param  {StyleBlockRecord} styleBlock
 * @param  {String[]}         options.breakpointIds    The list of media query keys to filter by (e.g. 'main', 'medium')
 * @param  {String[]}         options.customNodeStates   The list of custom node state names to filter by (e.g. 'current', 'open')
 * @param  {String[]}         options.pseudoStates    The list of pseudo states to filter by (e.g. 'hover', 'active')
 * @return {Object[]}                                 A list of style rule data embedded in the block.
 */
function pluckStyleRules_new(
  styleBlock: StyleBlock,
  options?: {
    breakpointIds: Array<BreakpointID>;
    customNodeStates: Array<CustomNodeStateKey>;
    pseudoStates: Array<PseudoStateKey>;
    variantStates: Array<VariantStateKey>;
    permuteAllStates?: boolean;
  }
) {
  const {
    breakpointIds = [],
    customNodeStates = [],
    pseudoStates = [],
    variantStates = [],
    permuteAllStates = false,
  } = options || {};
  if (!styleBlock) {
    return [];
  }

  if (!styleBlock.get('selector')) {
    throw new Error('StyleBlock must be decorated with selector!');
  }

  const rulesMap: Map<string, Variant> = new Map(
    _.toPairs({
      main: {
        styleLess: styleBlock.get('styleLess'),
        // @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'. | TS2339 - Property 'toJS' does not exist on type 'Selector'.
        selector: styleBlock.get('selector').toJS(),
      },
      ...styleBlock.get('variants').toJS(),
    })
  );

  const styleRules: Array<StyleRule> = [];
  let bestRule: StyleRule | null = null;
  for (const breakpointId of breakpointIds) {
    for (const variantState of [null, ...variantStates]) {
      for (const pseudoState of [null, ...pseudoStates]) {
        for (const customNodeState of [null, ...customNodeStates]) {
          const variantKey = createVariantKey({
            mediaQuery: breakpointId,
            customNodeState,
            pseudoState,
            variantState,
          });
          const variant = rulesMap.get(variantKey);
          if (variant) {
            const rule = createStyleRule({
              styleBlock: styleBlock.get('_id'),
              mediaQuery: breakpointId,
              customNodeState,
              pseudoState,
              variantState,
              styleLess: variant.styleLess,
              selector: variant.selector,
              origin: styleBlock.origin,
            });
            styleRules.push(rule);
            bestRule = rule;
            continue;
          }

          if (!bestRule || !permuteAllStates) {
            continue;
          }
          const chunks = createVariantChunks(
            bestRule,
            pseudoState,
            customNodeState,
            variantState,
            true
          );
          styleRules.push(
            createMintRule(
              styleBlock._id,
              chunks,
              breakpointId,
              pseudoState,
              customNodeState,
              variantState
            )
          );
        }
      }
    }
  }

  return styleRules;
}

const defaultStylesToStylesMap = (styles: WebflowCssProperties) =>
  (Object.keys(styles) as Array<keyof WebflowCssProperties>).reduce<
    Record<keyof WebflowCssProperties, any>
  >(
    (memo, key) => {
      memo[hyphenateStyleName(key) as keyof WebflowCssProperties] =
        convertStyleValueToString(styles, key);
      return memo;
    },
    {} as Record<keyof WebflowCssProperties, any>
  );

const defaultStylesToPropertyList = (styles: Record<any, any>) =>
  Object.keys(styles).map((property) => ({
    property,
    value: styles[property],
  }));

const createStyleRuleWithType = ({
  // @ts-expect-error - TS7031 - Binding element 'mediaQuery' implicitly has an 'any' type.
  mediaQuery,
  // @ts-expect-error - TS7031 - Binding element 'customNodeState' implicitly has an 'any' type.
  customNodeState,
  // @ts-expect-error - TS7031 - Binding element 'pseudoState' implicitly has an 'any' type.
  pseudoState,
  // @ts-expect-error - TS7031 - Binding element 'variantState' implicitly has an 'any' type.
  variantState,
  // @ts-expect-error - TS7031 - Binding element 'styles' implicitly has an 'any' type.
  styles,
  // @ts-expect-error - TS7031 - Binding element 'inheritedDistance' implicitly has an 'any' type.
  inheritedDistance,
  // @ts-expect-error - TS7031 - Binding element 'selector' implicitly has an 'any' type.
  selector,
  // @ts-expect-error - TS7031 - Binding element 'type' implicitly has an 'any' type.
  type,
}) => {
  if (
    inheritedDistance > 0 &&
    !containsTypographicProperties(defaultStylesToPropertyList(styles))
  )
    return null;

  return createStyleRule({
    mint: false,
    // @ts-expect-error probably should not be nully
    styleBlock: null,
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
    inheritedDistance,
    selector,
    type,
    styles,
  });
};

const createSelectorForDefaultRule = ({
  name,
  namespace,
  customNodeState,
  pseudoState,
}: {
  name: string;
  namespace?: string;
  customNodeState: CustomNodeStateKey | null;
  pseudoState: PseudoStateKey | null;
}) => {
  const chunk = {
    tokens: [
      buildSelectorToken({
        className: name,
        ...(namespace ? {namespace} : {}),
      }),
      ...getTokensForVariants({
        customNodeState,
        pseudoState,
        variantState: null,
      }),
    ],
    combinator: null,
  } as const;

  const chunks = [chunk];
  return {
    text: name,
    // @ts-expect-error - TS2345 - Argument of type '{ readonly tokens: readonly [SelectorToken, ...any[]]; readonly combinator: null; }[]' is not assignable to parameter of type 'SelectorChunk[]'.
    score: getSelectorScore(chunks),
    chunks,
  };
};

const classDefaultNames: BreakpointsMap<string> = {
  // TODO LargerBreakpoints - most probably we can get this information from somewhere
  [BREAKPOINT_ID_MAIN]: 'Desktop',
  [BREAKPOINT_ID_MEDIUM]: 'Tablet',
  [BREAKPOINT_ID_SMALL]: 'Phone Landscape',
  [BREAKPOINT_ID_TINY]: 'Phone Portrait',
  [BREAKPOINT_ID_LARGE]: '1280px',
  [BREAKPOINT_ID_XL]: '1440px',
  [BREAKPOINT_ID_XXL]: '1920px',
};

const createSelectorForIdRule = ({
  mediaQuery,
  customNodeState,
  pseudoState,
  variantState,
}: {
  mediaQuery: BreakpointID;
  customNodeState: CustomNodeStateKey | null;
  pseudoState: PseudoStateKey | null;
  variantState: VariantStateKey | null;
}) => {
  const name = classDefaultNames[mediaQuery] || 'Local Styles';
  const chunk = {
    tokens: [
      buildSelectorToken({
        className: name,
        namespace: 'id',
      }),
      ...getTokensForVariants({
        customNodeState,
        pseudoState,
        variantState,
      }),
    ],
    combinator: null,
  } as const;

  const chunks = [chunk];
  return {
    text: name,
    // @ts-expect-error - TS2345 - Argument of type '{ readonly tokens: readonly [SelectorToken, ...any[]]; readonly combinator: null; }[]' is not assignable to parameter of type 'SelectorChunk[]'.
    score: getSelectorScore(chunks),
    chunks,
  };
};

const createDefaultStyleRule = ({
  // @ts-expect-error - TS7031 - Binding element 'mediaQuery' implicitly has an 'any' type.
  mediaQuery,
  // @ts-expect-error - TS7031 - Binding element 'customNodeState' implicitly has an 'any' type.
  customNodeState,
  // @ts-expect-error - TS7031 - Binding element 'pseudoState' implicitly has an 'any' type.
  pseudoState,
  // @ts-expect-error - TS7031 - Binding element 'styles' implicitly has an 'any' type.
  styles,
  // @ts-expect-error - TS7031 - Binding element 'inheritedDistance' implicitly has an 'any' type.
  inheritedDistance,
  // @ts-expect-error - TS7031 - Binding element 'variantState' implicitly has an 'any' type.
  variantState,
}) =>
  createStyleRuleWithType({
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
    inheritedDistance,
    selector: createSelectorForDefaultRule({
      name: 'Default Styles',
      namespace: 'wf',
      customNodeState,
      pseudoState,
    }),
    styles,
    type: DEFAULT_STYLE_RULE,
  });

const createIdStyleRule = ({
  // @ts-expect-error - TS7031 - Binding element 'mediaQuery' implicitly has an 'any' type.
  mediaQuery,
  // @ts-expect-error - TS7031 - Binding element 'customNodeState' implicitly has an 'any' type.
  customNodeState,
  // @ts-expect-error - TS7031 - Binding element 'pseudoState' implicitly has an 'any' type.
  pseudoState,
  // @ts-expect-error - TS7031 - Binding element 'styles' implicitly has an 'any' type.
  styles,
  // @ts-expect-error - TS7031 - Binding element 'inheritedDistance' implicitly has an 'any' type.
  inheritedDistance,
  // @ts-expect-error - TS7031 - Binding element 'variantState' implicitly has an 'any' type.
  variantState,
}) => {
  return createStyleRuleWithType({
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
    inheritedDistance,
    selector: createSelectorForIdRule({
      mediaQuery,
      customNodeState,
      pseudoState,
      variantState,
    }),
    styles,
    type: LOCAL_STYLE_RULE,
  });
};

// @ts-expect-error - TS7006 - Parameter 'properties' implicitly has an 'any' type.
const containsTypographicProperties = (properties) =>
  // @ts-expect-error - TS7006 - Parameter 'prop' implicitly has an 'any' type.
  properties.some((prop) => {
    // We use "typographic" to signify that a property is inherited, so we use
    // it here for all CSS variables since they are also inherited (at least
    // with the way we currently author them -- @property/registerProperty
    // allows this to be configured but there is not widespread browser support
    // yet so we don't have plans to support this.)
    if (isCssVariableProperty(prop.property)) return true;

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Partial<Record<WebflowCssProperty, CssPropertyDetail>>'.
    const details = CSS_PROPERTY_DETAILS[prop.property];
    return details && details.typographic;
  });

// Helper: Creates the chunks for a new variant selector
const createVariantChunks = (
  bestRule: StyleRule,
  currentPseudo: null | PseudoStateKey,
  currentState: null | CustomNodeStateKey,
  currentVariantState: null | VariantStateKey,
  includePseudo = false
) => {
  // Need to deep clone here so we don't alias the underlying tokens
  const chunks = deepClone(bestRule.chunks);
  if (chunks === null) {
    return null;
  }
  // When auto-creating, we sometimes need to append additional tokens (e.g. .w--current) to the last chunk
  const newTokens = getTokensForVariants({
    customNodeState:
      bestRule.customNodeState !== currentState && currentState
        ? currentState
        : null,
    pseudoState:
      bestRule.pseudoState !== currentPseudo && includePseudo && currentPseudo
        ? currentPseudo
        : null,
    variantState:
      bestRule.variantState !== currentVariantState && currentVariantState
        ? currentVariantState
        : null,
  });

  const lastChunk = chunks[chunks.length - 1];
  // @ts-expect-error - Unchecked bounds
  lastChunk.tokens.push(...newTokens);

  return chunks;
};

// Helper: Constructs a brand new rule that should exist but does not
const createMintRule = (
  styleBlock: null | StyleBlockId,
  chunks: null | Array<SelectorChunk>,
  mediaQuery: BreakpointId,
  pseudoState: null | PseudoStateKey,
  customNodeState: null | CustomNodeStateKey,
  variantState: null | VariantStateKey
) =>
  createStyleRule({
    mint: true,
    // @ts-expect-error should not be falsy
    styleBlock,
    mediaQuery,
    customNodeState,
    pseudoState,
    variantState,
    selector: {
      score: getSelectorScore(chunks || emptyArray),
      chunks: chunks || emptyArray,
      text: '',
    },
  });

const _getStyleBlockStyleRulesWithCanonicalOrder = ({
  blockGuidsAffectingElement,
  blockGuidsAffectingAncestors,
  blockGuidsDistanceById,
  styleBlockState,
  appliedBlocks,
  selectedRichTextTag,
  currentPseudo,
  currentState,
  currentVariantState,
  currentMediaQuery,
  slugMap,
  variantDataMap,
  permuteAllStates,
}: {
  blockGuidsAffectingElement: Set<string>;
  blockGuidsAffectingAncestors: string[];
  blockGuidsDistanceById: {[key: string]: number};
  styleBlockState: StyleBlockStateType;
  appliedBlocks: string[];
  selectedRichTextTag: string | null;
  currentMediaQuery: BreakpointID;
  currentPseudo: PseudoStateKey | null;
  currentState: CustomNodeStateKey | null;
  currentVariantState: VariantStateKey | null;
  slugMap: Plugins.PluginsType['slugMap'];
  variantDataMap: VariantDataMap;
  permuteAllStates?: boolean;
}) => {
  const effectiveBreakpointIds =
    styleBlockState.getEffectiveBreakpointIds(currentMediaQuery);

  // Detect which style blocks apply directly to the selected node. In RTE mode, this walks through descendant tag blocks
  const directStyleBlockGuids = collectDirectStyleBlockGuids({
    blockGuidsAffectingElement,
    styleBlockState,
    selectedRichTextTag,
  });

  // IDs of all the StyleBlocks attached to ancestor nodes, since they can contribute inherited typographic styles to the current node
  // e.g. $('body') won't select <div class="button">, yet the button gets font/text styling from the "body { ... }" CSS selector.
  // In RTE mode, we also need to walk all global tag blocks to manually check if they apply
  const upstreamStyleBlockGuids = collectUpstreamStyleBlockGuids({
    blockGuidsAffectingAncestors,
    directStyleBlockGuids,
  });

  // Some pseudo states sometimes depend on other pseudo states, so check for their presence (e.g. :active should check for :active AND :hover)
  const pseudoStates = currentPseudo
    ? _.flatten([currentPseudo, getUpstreamPseudos(currentPseudo)])
    : emptyArray;

  const customNodeStates = currentState ? [currentState] : emptyArray;
  const variantStates = currentVariantState
    ? [currentVariantState]
    : emptyArray;

  // Collect inherited and cascading style rules that match the current MQ + state + pseudo state
  const filterParams = {
    styleBlockState,
    breakpointIds: effectiveBreakpointIds,
    pseudoStates,
    customNodeStates,
    variantStates,
  } as const;
  const upstreamStyleRules = collectRules({
    styleBlockIds: upstreamStyleBlockGuids,
    blockGuidsDistanceById,
    ...filterParams,
    onlyTypographic: true,
    slugMap,
    variantDataMap,
    permuteAllStates: false,
  });

  // Identify all the direct rules that come from StyleBlocks that are not applied to the current node
  // E.g. This can happen if a node has class="button primary", and another conflicting selector (E.g. "Primary.BUTTON") exists elswhere in the tree
  const foreignBlockIds = _.filter(
    directStyleBlockGuids,
    (guid) => appliedBlocks.indexOf(guid) === -1
  );

  // Collect direct style rules that match the current MQ + state + pseudo state
  const directStyleRules = collectRules({
    styleBlockIds: directStyleBlockGuids,
    foreignBlockIds,
    slugMap,
    variantDataMap,
    ...filterParams,
    permuteAllStates,
  });

  // Choose the non-inherited, non-foreign styleRule with the highest score as the best one (so far)
  let bestRule = getBestRule({
    rules: directStyleRules,
    mediaQuery: currentMediaQuery,
    pseudoState: currentPseudo,
    customNodeState: currentState,
    variantState: currentVariantState,
  });

  // If a direct rule doesn't exist for this media + state + pseudo combination, create it
  if (bestRule) {
    const bestRuleStyleBlock = bestRule.styleBlock;
    if (currentState !== null) {
      const exactStateVariantExists = directStyleRules.some((r) => {
        return (
          r.styleBlock === bestRuleStyleBlock &&
          r.mediaQuery === currentMediaQuery &&
          r.customNodeState === currentState &&
          r.pseudoState === null &&
          r.variantState === null
        );
      });

      if (!exactStateVariantExists) {
        const chunks = createVariantChunks(
          bestRule,
          currentPseudo,
          currentState,
          currentVariantState,
          false
        );
        bestRule = createMintRule(
          bestRule.styleBlock,
          chunks,
          currentMediaQuery,
          null,
          currentState,
          null
        );
        directStyleRules.push(bestRule);
      }
    }

    if (currentVariantState !== null) {
      const exactVariantStateVariantExists = directStyleRules.some((r) => {
        return (
          r.styleBlock === bestRuleStyleBlock &&
          r.mediaQuery === currentMediaQuery &&
          r.customNodeState === null &&
          r.pseudoState === null &&
          r.variantState === currentVariantState
        );
      });

      if (!exactVariantStateVariantExists) {
        const chunks = createVariantChunks(
          bestRule,
          currentPseudo,
          currentState,
          currentVariantState,
          false
        );
        bestRule = createMintRule(
          bestRule.styleBlock,
          chunks,
          currentMediaQuery,
          null,
          null,
          currentVariantState
        );
        directStyleRules.push(bestRule);
      }
    }

    const exactStatePseudoVariantExists = directStyleRules.some((r) => {
      return (
        r.styleBlock === bestRuleStyleBlock &&
        r.mediaQuery === currentMediaQuery &&
        r.customNodeState === currentState &&
        r.variantState === currentVariantState &&
        r.pseudoState === currentPseudo
      );
    });

    if (!exactStatePseudoVariantExists) {
      const chunks = createVariantChunks(
        bestRule,
        currentPseudo,
        currentState,
        currentVariantState,
        true
      );
      bestRule = createMintRule(
        bestRule.styleBlock,
        chunks,
        currentMediaQuery,
        currentPseudo,
        currentState,
        currentVariantState
      );
      directStyleRules.push(bestRule);
    }
  }

  const styleBlockRules = [...upstreamStyleRules, ...directStyleRules];

  const combinedBlockGuids = styleBlockRules
    .map((rule) => rule.styleBlock)
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  // This is the topological sort of all the relevant blocks as they would appear in the stylesheet,
  // needed to sort rules when they collide on specificity scores This is required because in CSS the
  // order in which selectors are defined matters, and later rules will override previous ones
  const orderedStyleBlocks = styleBlockState.getPartialOrderedList({
    leafGuids: combinedBlockGuids,
    includeFake: true,
  });

  const canonicalBlockOrder = _.fromPairs(
    orderedStyleBlocks.map((block, index) => [block.get('_id'), index])
  );

  return {canonicalBlockOrder, styleBlockRules};
};

const getStyleBlockStyleRules = ({
  expression,
  styleBlockState,
  appliedBlocks,
  pathToElement,
  selectedElementGuid,
  selectedRichTextTag,
  currentMediaQuery,
  currentPseudo,
  currentState,
  currentVariantState,
  slugMap,
  variantDataMap,
  permuteAllStates,
}: {
  expression: Expression;
  styleBlockState: StyleBlockStateType;
  appliedBlocks: string[];
  pathToElement: string[];
  selectedElementGuid: string;
  selectedRichTextTag: string | null;
  currentMediaQuery: BreakpointID;
  currentPseudo: PseudoStateKey | null;
  currentState: CustomNodeStateKey | null;
  currentVariantState: VariantStateKey | null;
  slugMap: Plugins.PluginsType['slugMap'];
  variantDataMap: VariantDataMap;
  permuteAllStates?: boolean;
}): {
  canonicalBlockOrder: _.Dictionary<number>;
  styleBlockRules: StyleRule[];
} => {
  const richTextMode = selectedRichTextTag !== null;

  // When inside RTE, the ancestor path is the full `pathToElement`, since it points to the parent Rich Text node
  // Otherwise, it's the entire path except for the tail node
  const ancestorDomPath = richTextMode
    ? pathToElement
    : pathToElement.slice(0, -1);

  // IDs of all the StyleBlocks that are apply to this node (or parent node in Rich Text Mode)
  const blockGuidsAffectingElement = calculateAffectedForElement({
    styleBlockState,
    expression,
    elementId: selectedElementGuid,
    slugMap,
  });

  const {blockGuidsAffectingAncestors, blockGuidsDistanceById} =
    calculateAffectedForAncestorPath({
      expression,
      styleBlockState,
      ancestorPath: ancestorDomPath,
      slugMap,
    });

  return _getStyleBlockStyleRulesWithCanonicalOrder({
    blockGuidsAffectingElement,
    blockGuidsAffectingAncestors,
    blockGuidsDistanceById,
    styleBlockState,
    appliedBlocks,
    selectedRichTextTag,
    currentPseudo,
    currentState,
    currentVariantState,
    currentMediaQuery,
    slugMap,
    variantDataMap,
    permuteAllStates,
  });
};

const getAncestorRulesForElement = ({
  mediaQuery,
  pseudoState,
  customNodeState,
  variantState,
  getters,
  breakpointIds,
}: {
  mediaQuery: BreakpointID;
  pseudoState?: PseudoStateKey | null;
  customNodeState?: CustomNodeStateKey | null;
  variantState: VariantStateKey | null;
  getters: RuleGetter[];
  breakpointIds: BreakpointID[];
}) => {
  const _getAncestorStyleRulesWithElement = (
    rootExpr: Expression,
    id: string,
    distance = 1
  ): StyleRule[] => {
    const element = Expressions.getParent(rootExpr, id);

    const styles = getRulesForElement({
      element,
      mediaQuery,
      pseudoState: pseudoState === null ? NO_PSEUDO : pseudoState,
      customNodeState,
      variantState,
      inheritedDistance: distance,
      getters,
      breakpointIds,
    });

    return element
      ? _getAncestorStyleRulesWithElement(
          rootExpr,
          element.id,
          distance + 1
        ).concat(styles ? styles : [])
      : emptyArray;
  };

  return _getAncestorStyleRulesWithElement;
};

export type RuleGetter = (arg1: {
  element: ExpressionElement;
  mediaQuery: BreakpointID;
  pseudoState: PseudoStateKey;
  customNodeState: CustomNodeStateKey | null;
  variantState: VariantStateKey | null;
  inheritedDistance: number;
}) => Array<StyleRule | null>;

const getAllRulesForElement = ({
  expression,
  selectedElementGuid,
  mediaQuery,
  pseudoState,
  customNodeState,
  variantState,
  getters,
  breakpointIds,
}: {
  expression: Expression;
  selectedElementGuid: string;
  mediaQuery: BreakpointID;
  pseudoState?: PseudoStateKey | null;
  customNodeState?: CustomNodeStateKey | null;
  variantState: VariantStateKey | null;
  getters: RuleGetter[];
  breakpointIds: BreakpointID[];
}) => {
  const element = Expressions.getElementById(expression, selectedElementGuid);

  const directRules = getRulesForElement({
    element,
    mediaQuery,
    pseudoState: pseudoState === null ? NO_PSEUDO : pseudoState,
    customNodeState,
    variantState,
    inheritedDistance: 0,
    getters,
    breakpointIds,
  });

  const upstreamRules = getAncestorRulesForElement({
    mediaQuery,
    pseudoState,
    customNodeState,
    variantState,
    getters,
    breakpointIds,
  })(expression, selectedElementGuid);

  return [...directRules, ...upstreamRules];
};

const getRulesForElement = ({
  element,
  mediaQuery: currentMediaQuery,
  pseudoState,
  customNodeState = null,
  variantState,
  inheritedDistance = 0,
  getters,
  breakpointIds,
}: {
  element: ExpressionElement | null | undefined;
  mediaQuery: BreakpointID;
  pseudoState?: PseudoStateKey | null;
  customNodeState?: CustomNodeStateKey | null;
  variantState: VariantStateKey | null;
  inheritedDistance: number;
  getters: RuleGetter[];
  breakpointIds: BreakpointID[];
}): StyleRule[] => {
  if (!element) return emptyArray;

  const appliedRules: StyleRule[] = [];

  const numBreakpoints = breakpointIds.length;

  for (let i = 0; i < numBreakpoints; i++) {
    const mediaQuery = breakpointIds[i];
    const numGetters = getters.length;

    if (mediaQuery) {
      for (let j = 0; j < numGetters; j++) {
        const getter = getters[j];

        if (getter) {
          const rules = getter({
            element,
            mediaQuery,
            pseudoState: NO_PSEUDO,
            customNodeState,
            variantState,
            inheritedDistance,
          });

          const numRules = rules.length;

          for (let k = 0; k < numRules; k++) {
            const rule = rules[k];
            if (rule) appliedRules.push(rule);
          }
        }
      }

      if (pseudoState && pseudoState !== NO_PSEUDO) {
        for (let j = 0; j < numGetters; j++) {
          const getter = getters[j];

          if (getter) {
            const rules = getter({
              element,
              mediaQuery,
              pseudoState,
              customNodeState,
              variantState,
              inheritedDistance,
            });

            const numRules = rules.length;

            for (let k = 0; k < numRules; k++) {
              const rule = rules[k];
              if (rule) appliedRules.push(rule);
            }
          }
        }
      }
    }

    if (mediaQuery === currentMediaQuery) break;
  }

  return appliedRules;
};

const getMatchingConditionalStates = (
  element: ExpressionElement,
  conditionalStyles: {
    [key: string]: {
      [key: string]: Record<any, any>;
    };
  }
): Array<[string, string]> => {
  const stateValueArray: Array<[string, string]> = [];

  for (const key in conditionalStyles) {
    const value = Object.getOwnPropertyNames(conditionalStyles[key])[0];

    if (
      Expressions.getElementDataAt(
        element,
        [{in: 'Record', at: key}],
        'Text',
        ''
      ) === value
    ) {
      stateValueArray.push([key, value]);
    }
  }

  // [['state', 'value'], ['state2', 'value2'], ...];
  return stateValueArray;
};

const getAggregatedConditionalStyles = (
  atom: NormalizedAtomConfig,
  states: Array<[string, string]>,
  mediaQuery: BreakpointID,
  pseudoState:
    | PseudoStateKey
    | (typeof REDIRECTED_PSEUDOS)[keyof typeof REDIRECTED_PSEUDOS]
) => {
  const styles: Record<string, any> = {};

  for (const [key, value] of states) {
    const conditionalStyle = _.get(atom, [
      'style',
      'conditional',
      key,
      value,
      mediaQuery,
      pseudoState,
    ]);
    if (conditionalStyle) {
      Object.assign(styles, conditionalStyle);
    }
  }

  return styles;
};

const getPluginComponentRules: (plugins: Plugins.PluginsType) => RuleGetter =
  (plugins) =>
  ({
    element,
    mediaQuery,
    pseudoState,
    variantState,
    customNodeState,
    inheritedDistance,
  }) => {
    if (isPluginComponentInst(plugins, element)) {
      const component = getPluginComponent(plugins, element);
      const styleConfig = component ? Component.getStyle(component) : null;

      if (styleConfig) {
        return createRulesFromDefaultConfig({
          styleConfig,
          mediaQuery,
          pseudoState,
          customNodeState,
          variantState,
          inheritedDistance,
        });
      }
    }

    return emptyArray;
  };

// TODO: Add support for conditional rules.
function createRulesFromDefaultConfig({
  styleConfig,
  mediaQuery,
  pseudoState,
  customNodeState,
  variantState,
  inheritedDistance,
}: {
  styleConfig: DefaultBaseStyleConfig;
  mediaQuery: BreakpointID;
  pseudoState: PseudoStateKey;
  customNodeState: CustomNodeStateKey | null;
  variantState: VariantStateKey | null;
  inheritedDistance: number;
}) {
  const path = [
    ...(variantState ? ['variants', variantState] : ['base']),
    mediaQuery,
    pseudoState,
  ];
  const baseStyles = _.get(styleConfig, path);

  if (baseStyles) {
    const styleRules: Array<StyleRule | null> = [];
    styleRules.push(
      createDefaultStyleRule({
        mediaQuery,
        inheritedDistance,
        pseudoState: pseudoState === NO_PSEUDO ? null : pseudoState,
        customNodeState,
        variantState,
        styles: defaultStylesToStylesMap(baseStyles),
      })
    );
    return styleRules;
  }
  return emptyArray;
}

type PseudoStateTuple =
  | [
      PseudoStateKey,
      (
        | PseudoStateKey
        | (typeof REDIRECTED_PSEUDOS)[keyof typeof REDIRECTED_PSEUDOS]
      ),
    ]
  | [PseudoStateKey];

const getRedirectedPseudoStates = (
  pseudoState: PseudoStateKey,
  customNodeState: CustomNodeStateKey | null
): PseudoStateTuple => {
  return customNodeState === REDIRECTED_PSEUDOS.CHECKED
    ? [pseudoState, REDIRECTED_PSEUDOS.CHECKED]
    : customNodeState === REDIRECTED_PSEUDOS.FOCUS
      ? [pseudoState, REDIRECTED_PSEUDOS.FOCUS]
      : customNodeState === REDIRECTED_PSEUDOS.FOCUS_VISIBLE
        ? [pseudoState, REDIRECTED_PSEUDOS.FOCUS_VISIBLE]
        : customNodeState === REDIRECTED_PSEUDOS.ECOMMERCE_PILL_SELECTED
          ? [pseudoState, REDIRECTED_PSEUDOS.ECOMMERCE_PILL_SELECTED]
          : customNodeState === REDIRECTED_PSEUDOS.ECOMMERCE_PILL_DISABLED
            ? [pseudoState, REDIRECTED_PSEUDOS.ECOMMERCE_PILL_DISABLED]
            : customNodeState ===
                REDIRECTED_PSEUDOS.ECOMMERCE_ADD_TO_CART_DISABLED
              ? [pseudoState, REDIRECTED_PSEUDOS.ECOMMERCE_ADD_TO_CART_DISABLED]
              : customNodeState ===
                  REDIRECTED_PSEUDOS.ECOMMERCE_BUY_NOW_DISABLED
                ? [pseudoState, REDIRECTED_PSEUDOS.ECOMMERCE_BUY_NOW_DISABLED]
                : [pseudoState];
};

const getDefaultRules: (plugins: Plugins.PluginsType) => RuleGetter =
  (plugins) =>
  ({
    element,
    mediaQuery,
    pseudoState,
    variantState,
    customNodeState,
    inheritedDistance,
  }) => {
    const atom =
      element && element.type ? Plugins.getAtom(plugins, element) : null;

    if (atom) {
      const path = [
        'style',
        ...(variantState ? ['variants', variantState] : ['base']),
        mediaQuery,
        pseudoState,
      ];
      const styles = _.get(atom, path);
      const conditionalStyles = _.get(atom, ['style', 'conditional']);

      if (!styles && !conditionalStyles) return emptyArray;

      const styleRules: Array<StyleRule | null> = [];

      if (styles) {
        styleRules.push(
          createDefaultStyleRule({
            mediaQuery,
            inheritedDistance,
            pseudoState: pseudoState === NO_PSEUDO ? null : pseudoState,
            customNodeState,
            variantState,
            styles: defaultStylesToStylesMap(styles),
          })
        );
      }

      if (conditionalStyles) {
        const matchingStates = getMatchingConditionalStates(
          element,
          conditionalStyles
        );

        const states = getRedirectedPseudoStates(pseudoState, customNodeState);

        states.forEach((state) => {
          const matchingStatesStyles = getAggregatedConditionalStyles(
            atom,
            matchingStates,
            mediaQuery,
            state
          );

          if (matchingStatesStyles) {
            styleRules.push(
              createDefaultStyleRule({
                mediaQuery,
                inheritedDistance,
                pseudoState: pseudoState === NO_PSEUDO ? null : pseudoState,
                customNodeState,
                variantState,
                styles: defaultStylesToStylesMap(matchingStatesStyles),
              })
            );
          }
        });
      }

      return styleRules;
    }

    return emptyArray;
  };

const expressionToPojo = (expression: Expression): Json =>
  matchExpression<Json>({
    Boolean: identity,
    Number: identity,
    Text: identity,
    List: (items) => items.map(expressionToPojo),
    Record: (fields) => {
      const result: Record<string, any> = {};

      for (const k of Object.keys(fields)) {
        const expr = fields[k];
        if (expr) {
          result[k] = expressionToPojo(expr);
        }
      }

      return result;
    },
    _: constant(null),
  })(expression);

const _getElementIdRules: RuleGetter = ({
  element,
  mediaQuery,
  pseudoState,
  customNodeState,
  inheritedDistance,
  variantState,
}) => {
  // Generate variant or base path based on variant state
  const variantOrBasePath: ExpressionPath = variantState
    ? [
        {in: 'Record', at: 'variants'},
        {in: 'Record', at: variantState},
      ]
    : [{in: 'Record', at: 'base'}];

  const stylePath = [
    {in: 'Record', at: 'style'},
    ...variantOrBasePath,
    {in: 'Record', at: mediaQuery},
    {in: 'Record', at: pseudoState},
  ] satisfies ExpressionPath;

  const elementData = Expressions.getElementData(element);
  if (!elementData) return emptyArray;

  const styleExpr = Expressions.getIn(elementData, stylePath);
  if (!styleExpr) return emptyArray;

  const styleRecord = Expressions.getRecord(styleExpr);
  if (!styleRecord) return emptyArray;

  const styles = Object.entries(styleRecord).reduce<WebflowCssProperties>(
    (acc, [key, expr]) => {
      if (!expr) return acc;

      const value = expressionToPojo(expr);
      if (value !== undefined && value !== null && value !== '') {
        const cssKey = key as keyof WebflowCssProperties;
        acc[cssKey] = value;
      }
      return acc;
    },
    {}
  );

  // Return style rule with processed styles
  return [
    createIdStyleRule({
      mediaQuery,
      inheritedDistance,
      pseudoState: pseudoState === NO_PSEUDO ? null : pseudoState,
      variantState,
      customNodeState,
      styles: defaultStylesToStylesMap(styles),
    }),
  ];
};

const getIdRules: RuleGetter = ({
  element,
  mediaQuery,
  pseudoState,
  customNodeState,
  inheritedDistance,
  variantState,
}) => {
  const baseIdRules = _getElementIdRules({
    element,
    mediaQuery,
    pseudoState,
    customNodeState,
    inheritedDistance,
    variantState: null,
  });

  return variantState
    ? [
        ...baseIdRules,
        ..._getElementIdRules({
          element,
          mediaQuery,
          pseudoState,
          customNodeState,
          inheritedDistance,
          variantState,
        }),
      ]
    : baseIdRules;
};

const cacheSize = 50;

const cacheKey =
  <A, K extends number | string>(getKey: (a: A) => K) =>
  <V extends object | number | string>(fn: (a: A) => V) => {
    const cache = new LRUCache<K, V>({max: cacheSize});

    return function (arg: A): V {
      const hash = getKey(arg);

      if (!cache.has(hash)) {
        cache.set(hash, fn(arg));
      }

      return cache.get(hash)!;
    };
  };

const memo50 = cacheMemo(cacheSize);
const memoArrayOfId = cacheKey((arr: string[]): string =>
  arr ? arr.join(',') : ''
);

function _validatePathToElement(
  pathToElement: string[]
): asserts pathToElement is NonNullable<typeof pathToElement> {
  if (!pathToElement) {
    throw new Error(
      'Invalid params! pathToNode was ' + JSON.stringify(pathToElement, null, 2)
    );
  }

  if (!pathToElement.length) {
    throw new Error(
      'Invalid params! pathToNode.length was ' +
        JSON.stringify(pathToElement.length, null, 2)
    );
  }
}

function _validateCurrentMediaQuery(
  currentMediaQuery: BreakpointID | null | undefined
): asserts currentMediaQuery is NonNullable<typeof currentMediaQuery> {
  if (!currentMediaQuery) {
    // JSON.stringify can return `undefined`, but Flow does not allow the coercion of `undefined`
    // into a string for string templates or concatenation, thus we supply 'undefined' explicitly.
    const stringifiedQuery = JSON.stringify(currentMediaQuery, null, 2);
    const invalidParams = stringifiedQuery ? stringifiedQuery : 'undefined';

    throw new Error('Invalid params! currentMediaQuery was ' + invalidParams);
  }
}

function _validateStyleBlockState(
  styleBlockState: StyleBlockStateType
): asserts styleBlockState is NonNullable<typeof styleBlockState> {
  if (!styleBlockState) {
    throw new Error(
      'Invalid params! styleBlockState was ' +
        JSON.stringify(styleBlockState, null, 2)
    );
  }
}

const getSortedRules = (
  allRules: StyleRule[],
  styleBlockState: StyleBlockStateType,
  canonicalBlockOrder: Dictionary<number>
): StyleRule[] =>
  // Sort rules by direct/inherited -> specificity score -> mediaQuery -> canonical order
  sortStyleRules(allRules, {
    queryOrder: styleBlockState.getOrderedBreakpointIds(),
    blockOrder: canonicalBlockOrder,
    styleBlocks: styleBlockState.styleBlocks,
  });

const createGetLinkedRulesForElement = (permuteAllStates: boolean) =>
  weakMemo((plugins: Plugins.PluginsType) =>
    memoArrayOfId((pathToElement: string[]) =>
      memoArrayOfId((appliedBlocks: string[] | undefined = []) =>
        memo50((selectedRichTextTag?: string) =>
          memo50((currentMediaQuery?: null | BreakpointID) =>
            memo50((currentState: null | CustomNodeStateKey = null) =>
              memo50((currentPseudo: null | PseudoStateKey = null) =>
                memo50((currentVariantState: null | VariantStateKey = null) =>
                  weakMemo((styleBlockState: StyleBlockStateType) =>
                    weakMemo((expression: Expression | null) =>
                      weakMemo((componentMap: Component.ComponentMap) => {
                        _validatePathToElement(pathToElement);
                        _validateCurrentMediaQuery(currentMediaQuery);
                        _validateStyleBlockState(styleBlockState);
                        if (!expression) {
                          throw new Error(
                            'Invalid params! expression was ' +
                              JSON.stringify(expression, null, 2)
                          );
                        }

                        // Casting to `string` as there's a check above to ensure `pathToNode` is not empty.
                        const selectedElementGuid = _.last(
                          pathToElement
                        ) as string;

                        const rules = getAllRulesForElement({
                          expression,
                          selectedElementGuid,
                          mediaQuery: currentMediaQuery,
                          pseudoState: currentPseudo,
                          customNodeState: currentState,
                          variantState: currentVariantState,
                          breakpointIds:
                            styleBlockState.getOrderedBreakpointIds(),
                          getters: [
                            getDefaultRules(plugins),
                            getPluginComponentRules(plugins),
                            getIdRules,
                          ],
                        });
                        const slugMap = Plugins.getPluginsSlugMap(plugins);
                        const {styleBlockRules, canonicalBlockOrder} =
                          getStyleBlockStyleRules({
                            expression,
                            styleBlockState,
                            appliedBlocks,
                            pathToElement,
                            selectedElementGuid,
                            selectedRichTextTag: selectedRichTextTag || null,
                            currentMediaQuery,
                            currentPseudo,
                            currentState,
                            currentVariantState,
                            slugMap,
                            variantDataMap:
                              generateVariantDataMap(componentMap),
                            permuteAllStates,
                          });

                        return getSortedRules(
                          [...rules, ...styleBlockRules],
                          styleBlockState,
                          canonicalBlockOrder
                        );
                      })
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  );

const getLinkedRulesForElement = createGetLinkedRulesForElement(false);
const getLinkedRulesForElementPermutingStates =
  createGetLinkedRulesForElement(true);

/**
 * Calculates an ordered list of relevant StyleRules that contribute styles to a DomNode.
 * @param  {String[]}             options.pathToElement           An ordered path of DomNodes guids from the root <body> to the currently-selected node. The last node is assumed to be the current selected DomNode.
 *                                                                (If selectedRichTextTag is specified, however, the last node should be the outer Rich Text wrapper node.)
 * @param  {String[]}             options.appliedBlocks           The list of StyleBlock guids that are currently attached to the selected node (which should be the tail item in pathToElement).
 *                                                                (If selectedRichTextTag is specified, however, this should be the list of applied blocks to the outer Rich Text wrapper node.)
 * @param  {String}               options.selectedRichTextTag     When specified, indicates that we've selected a content node with this HTML tag name inside of an Rich Text element.
 * @param  {String}               options.currentMediaQuery       The current active media query key, e.g. 'main', 'medium', etc.
 * @param  {STATES}               options.currentState            The current Webflow-special state on the node (e.g. w--current), if any. Can be derived using SelectorUtils.detectStateFromClasses()
 * @param  {PSEUDOS}              options.currentPseudo           If the StyleTab is currently in pseudo-mode, the current active pseudo.
 * @param  {StyleBlockState}      options.styleBlockState         The source of truth for all style information.
 * @param  {Component.ComponentMap} options.componentMap          The map of components.
 * @return {Object[]}                                             The ordered list of style rules that contribute styles to this element.
 */
export const getLinkedRules = ({
  pathToElement,
  appliedBlocks,
  selectedRichTextTag,
  currentMediaQuery,
  currentState,
  currentPseudo,
  currentVariantState,
  styleBlockState,
  expression,
  plugins,
  componentMap,
  permuteAllStates = false,
}: {
  pathToElement: string[];
  appliedBlocks?: string[];
  selectedRichTextTag?: string;
  currentMediaQuery?: null | BreakpointID;
  currentState?: null | CustomNodeStateKey;
  currentPseudo?: null | PseudoStateKey;
  currentVariantState?: null | VariantStateKey;
  styleBlockState: StyleBlockStateType;
  expression: Expression | null;
  plugins: Plugins.PluginsType;
  componentMap: Component.ComponentMap;
  permuteAllStates?: boolean;
}): StyleRule[] =>
  (permuteAllStates
    ? getLinkedRulesForElementPermutingStates
    : getLinkedRulesForElement)(plugins)(pathToElement)(appliedBlocks!)(
    selectedRichTextTag
  )(currentMediaQuery)(currentState)(currentPseudo)(currentVariantState)(
    styleBlockState
  )(expression)(componentMap);

/**
 * Walks through a list of StyleBlocks and collects all the appropriate StyleRule data.
 * @param  {String[]}          options.styleBlockIds    A list of StyleBlock GUIDs we care about.
 * @param  {String[]}          options.foreignBlockIds  A list of StyleBlock GUIDs that contribute styles, but are not directly attached to the current node.
 * @param  {StyleBlocksState}  options.styleBlockState  The main store of all StyleBlocks.
 * @param  {String[]}          options.breakpointIds     The list of media query keys we care about.
 * @param  {String[]}          options.pseudoStates     The list of pseudo state keys we care about.
 * @param  {Boolean}           options.onlyTypographic  If true, only StyleRules that have typographic styles defined will be returned.
 * @return {Object[]}                                   A list of StyleRule objects, sorted by score.
 */
function collectRules(options: {
  styleBlockIds: Array<string>;
  foreignBlockIds?: Array<string>;
  styleBlockState: StyleBlockStateType;
  breakpointIds: Array<BreakpointID>;
  pseudoStates: Array<PseudoStateKey>;
  customNodeStates: Array<CustomNodeStateKey>;
  variantStates: Array<VariantStateKey>;
  onlyTypographic?: boolean;
  blockGuidsDistanceById?: {
    [key: string]: number;
  };
  slugMap: Plugins.PluginsType['slugMap'];
  variantDataMap: VariantDataMap;
  permuteAllStates?: boolean;
}): Array<StyleRule> {
  const {
    styleBlockIds,
    foreignBlockIds = [],
    styleBlockState,
    breakpointIds,
    pseudoStates,
    customNodeStates,
    variantStates,
    onlyTypographic = false,
    blockGuidsDistanceById,
    slugMap,
    variantDataMap,
    permuteAllStates,
  } = options;
  const foreignBlockIdMap = _.keyBy(foreignBlockIds);

  const styleRules = _.flatten(
    styleBlockIds.map((blockGuid) => {
      const blockForGuid = styleBlockState.getBlock(blockGuid, {
        decorate: true,
        slugMap,
        variantDataMap,
      });
      if (blockForGuid == null) {
        return null;
      }
      // An inactive library's tag style keeps mode applications, not styling.
      const styleBlock = styleBlockState.isHiddenLibraryTagStyle(blockGuid)
        ? removeHiddenStyleBlockTagStyling(blockForGuid)
        : blockForGuid;
      if (styleBlock == null) {
        return null;
      }
      const blockRules: StyleRule[] = permuteAllStates
        ? pluckStyleRules_new(styleBlock, {
            breakpointIds,
            pseudoStates,
            customNodeStates,
            variantStates,
            permuteAllStates,
          })
        : pluckStyleRules_old(styleBlock, {
            breakpointIds,
            pseudoStates,
            customNodeStates,
            variantStates,
          });

      return blockRules.map<StyleRule | null>((rule) => {
        if (onlyTypographic) {
          if (
            containsTypographicProperties(cssToPropertyList(rule.styleLess))
          ) {
            if (blockGuidsDistanceById) {
              // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
              rule.inheritedDistance = blockGuidsDistanceById[blockGuid];
            }
          } else {
            // Ignore non-typographic StyleRules when we're only looking for upstream cascading styles
            return null;
          }
        }

        // Mark class blocks that are known to be foreign (contribute styles to the current node, but aren't atteched directly to it)
        if (
          !rule.inherited &&
          styleBlock.get('type') === 'class' &&
          foreignBlockIdMap[blockGuid]
        ) {
          rule.foreign = true;
        }

        if (rule.styleBlock != null) {
          const styles = getStylesFromStyleBlock({
            styleBlock,
            mediaQuery: rule.mediaQuery,
            customNodeState: rule.customNodeState,
            pseudoState: rule.pseudoState,
            variantState: rule.variantState,
          });
          if (styles != null) {
            rule.styles = cssToPropertyMap(styles);
          }
        }

        // Remove .styleLess from the rule since we only need it here for typographic style detection
        // It can later be looked up via StyleBlockState.getStyles() if needed
        // @ts-expect-error - TS2790 - The operand of a 'delete' operator must be optional.
        delete rule.styleLess; // eslint-disable-line webflow/no-delete

        return rule;
      });
    })
  ).filter((rule): rule is StyleRule => rule != null);

  return sortStyleRules(styleRules, {
    queryOrder: breakpointIds,
    blockOrder: styleBlockIds.reduce((acc: {[k: string]: number}, id, idx) => {
      acc[id] = idx;
      return acc;
    }, {}),
    styleBlocks: styleBlockState.styleBlocks,
  });
}

/**
 * Collects all the StyleBlock guids that directly apply to a node.
 * @param  {String[]}         options.blockGuidsAffectingElement  The list of blocks that affect an element.
 *                                                                If in Rich Text mode, this refers to the blocks that are attached to the parent RTE node.
 * @param  {StyleBlockState}  options.styleBlockState             The source of truth for all style information.
 * @param  {String}           options.selectedRichTextTag         If specified, indicates that we've selected a content node with this HTML tag name inside of an Rich Text element.
 * @return {String[]}                                             An array of StyleBlock guids that *directly* apply to the selected node.
 */
function collectDirectStyleBlockGuids(options: {
  blockGuidsAffectingElement: Set<string>;
  styleBlockState: StyleBlockStateType;
  selectedRichTextTag: null | string;
}): Array<string> {
  const {
    blockGuidsAffectingElement,
    styleBlockState,
    selectedRichTextTag = null,
  } = options;
  // If we are not in Rich Text mode, just return the directly applied guids
  if (selectedRichTextTag === null) {
    return [...blockGuidsAffectingElement];
  }

  // If we're inside a Rich Text node, then the direct StyleBlocks are actually attached to the parent,
  // and should actually be upstream instead. Need to go through each applicable block's descendant
  // tag selectors and see if any match the currently selected rich text
  const descendantTagBlockGuids: Array<any | string> = [];

  for (const blockGuid of blockGuidsAffectingElement) {
    const childBlocks = styleBlockState
      .getChildren(blockGuid, {decorate: false})
      .toJS();

    const childBlocksTyped: Array<StyleBlock> = childBlocks as any;

    for (const childBlock of childBlocksTyped) {
      if (
        childBlock.type === 'tag' &&
        childBlock.name === selectedRichTextTag.toLowerCase() &&
        childBlock.comb === COMBINATORS.DESCENDANT
      ) {
        descendantTagBlockGuids.push(childBlock._id);
      }
    }
  }

  // We also need to walk through all global type==tag && name==selectedRichTextTag blocks and include them,
  // because Rich Text content nodes are never recorded inside the AffectedNodeStore and will not be part
  // of the passed-in blockGuidsAffectingElement set
  const matchingDefaultTagBlockGuid = styleBlockState.globalOrder.find(
    // @ts-expect-error - TS2345 - Argument of type '(globalBlockGuid: string | undefined) => boolean | null | undefined' is not assignable to parameter of type '(value?: string | undefined, key?: string | undefined, iter?: Iterable<string, string> | undefined) => boolean'.
    (globalBlockGuid) => {
      const styleBlock = styleBlockState.getBlock(globalBlockGuid);
      return (
        styleBlock &&
        styleBlock.get('type') === 'tag' &&
        styleBlock.get('name') === selectedRichTextTag
      );
    }
  );

  // The default tag block should always go first because RTE descendant tag blocks are always guaranteed to come later in CSS render order
  return _.compact([matchingDefaultTagBlockGuid, ...descendantTagBlockGuids]);
}

/**
 * Collects all the StyleBlock guids that affect the current node indirectly via inheritance.
 * @param  {String[]} options.blockGuidsAffectingAncestors  The list of block GUIDs from AffectedNodeStore that affect all ancestors.
 * @param  {String[]} options.directStyleBlockGuids         The list of block GUIDs that directly apply to the selected element, from collectDirectStyleBlockGuids()
 * @return {String[]}
 */
function collectUpstreamStyleBlockGuids({
  blockGuidsAffectingAncestors,
  directStyleBlockGuids,
}: {
  blockGuidsAffectingAncestors: Array<string>;
  directStyleBlockGuids: Array<string>;
}): Array<string> {
  return _.uniq(
    _.without(blockGuidsAffectingAncestors, ...directStyleBlockGuids)
  );
}

/**
 * Selects the best non-inherited, non-foreign style rule from a list of linked rules that has been pre-sorted in ascending specificity order.
 * Attempts to select a rule with the given variant config if the best rule's style block has an existing variant that matches.
 * If no variant config match or no match parameters specified, simply selects the last (highest-specificity) rule.
 * This is mainly useful for preserving expected user behavior for rule matching when toggling selector options.
 * @param {Object} options
 *   rules         {StyleRule[]} The list of style rules to choose from. Must be pre-sorted in ascending order of specificity.
 *   mediaQuery    {String}      The media query key to prefer for the best rule (optional).
 *   pseudoState   {PSEUDOS}     The pseudo state to prefer for the best rule (optional).
 *   customNodeState  {STATES}      The custom node state to prefer for the best rule (optional).
 * @return {Object}              The best matched rule. If all are inherited or foreign, returns null.
 */
export function getBestRule(options: {
  rules: Array<StyleRule>;
  mediaQuery?: null | BreakpointID;
  pseudoState?: null | PseudoStateKey;
  customNodeState?: null | CustomNodeStateKey;
  variantState: null | VariantStateKey;
  /**
   * If provided, rules with an `origin` may be selected as the best rule if
   * they belong to a library with the `view` capability.
   */
  librariesCapabilitiesMap?: LibrariesCapabilitiesMap;
}): null | StyleRule {
  const {
    rules,
    mediaQuery,
    pseudoState,
    customNodeState,
    librariesCapabilitiesMap,
  } = options;
  const bestRuleCandidates = _.filter(
    rules,
    (rule) =>
      !rule.inherited &&
      !rule.foreign &&
      rule.type === STYLE_BLOCK_STYLE_RULE &&
      (rule.origin == null ||
        (librariesCapabilitiesMap != null &&
          hasCapability({
            action: 'view',
            origin: rule.origin,
            librariesCapabilitiesMap,
          })))
  );
  if (bestRuleCandidates.length > 0) {
    const bestRule = _.last(bestRuleCandidates);
    const sameBlockAndStateCandidates = bestRuleCandidates.filter(
      (rule) =>
        bestRule &&
        rule.styleBlock === bestRule.styleBlock &&
        (mediaQuery === undefined || rule.mediaQuery === mediaQuery) &&
        (pseudoState === undefined || rule.pseudoState === pseudoState) &&
        (customNodeState === undefined ||
          rule.customNodeState === customNodeState)
    );
    if (sameBlockAndStateCandidates.length > 0) {
      // Interesting case where if user sets style using All Links tag on a link that has a 'current' state (ex. Tab Link), the tag +
      // current combo becomes the rule with highest-specificity. However, because it is a tag, the Selector ends up with null for selected rule
      // which causes an empty Selector display when the user adds a new class and new classes generated with every style change. This makes sure
      // that if there is a default-a + current rule, and there is another class option, that option is selected.
      if (
        bestRule &&
        getType(bestRule.chunks) === TAG &&
        getTagName(bestRule.chunks) === 'a' &&
        bestRule.customNodeState === CURRENT &&
        bestRuleCandidates.length > 2
      ) {
        return _.nth(bestRuleCandidates, -2) ?? null;
      }
      return _.last(sameBlockAndStateCandidates) ?? null;
    } else {
      return bestRule ?? null;
    }
  } else {
    return null;
  }
}

const isClassStyleBlock = (styleBlock: StyleBlock) =>
  styleBlock.get('type') === 'class';

// Remove trailing digits from "Heading N"-style labels
// e.g. "Heading 2" => "Heading"
const trimTrailingDigits = (string: string): string => {
  return string.replace(/\d$/, '').trim();
};

const toStyleBlockNames = (
  styleBlocks: ImmutableMap<string, StyleBlock>
): Array<string> =>
  styleBlocks.reduce<Array<any>>((acc, styleBlock) => {
    if (isClassStyleBlock(styleBlock)) {
      acc.push(styleBlock.name);
    }
    return acc;
  }, []);

export const getAutoGeneratedClassSuggestion = ({
  selectedElement,
  currentComponent,
  componentMap,
  plugins,
  styleBlockState,
  autoCreatePreference,
}: {
  selectedElement: ExpressionElement;
  currentComponent: Component.ComponentType;
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
  styleBlockState: StyleBlockStateType;
  autoCreatePreference: AutoCreatePreference;
}): SuggestionType => {
  const newClassName =
    autoCreatePreference === 'element'
      ? 'Element'
      : generateNewClassName({
          baseClassName: trimTrailingDigits(
            getElementLabel(
              selectedElement,
              Component.getRender(currentComponent),
              componentMap,
              plugins
            )
          ),
          styleBlockNames: toStyleBlockNames(styleBlockState.styleBlocks),
        });

  return {
    _id: null,
    type: autoCreatePreference,
    create: true,
    global: true,
    fuzzy: newClassName,
    name: newClassName,
  };
};

export const getOrCreateRule = ({
  styleBlockState,
  selectedElement,
  newStyleBlockId,
  userId,
  externalStateRule,
  externalStateMediaQuery,
  externalStatePseudoMode,
  externalStateCustomNodeState,
  externalStateVariantState,
  designerState,
  uiNodeState,
  autoCreatePreference,
}: {
  styleBlockState: StyleBlockStateType;
  selectedElement: ExpressionElement;
  newStyleBlockId: string;
  userId: string;
  externalStateRule: StyleRule | null | undefined;
  externalStateMediaQuery: BreakpointID | null | undefined;
  externalStatePseudoMode?: PseudoStateKey | null | undefined;
  externalStateCustomNodeState?: CustomNodeStateKey | null | undefined;
  externalStateVariantState: VariantStateKey | null | undefined;
  designerState: DesignerStateType;
  uiNodeState: UiNodeStateType;
  autoCreatePreference: AutoCreatePreference;
}): {
  styleBlockState: StyleBlockStateType;
  ruleWasCreated: boolean;
  rule: StyleRule;
} => {
  const plugins = getPluginsFromState({DesignerStore: designerState});

  // If we already have a rule selected, get these
  // parameters from the currently-selected rule.
  //
  // Otherwise, we can assume MQ is current, and other
  // states (customNodeState/pseduoState) are un-set, because
  // we're auto-generating a new class:
  if (
    externalStateRule &&
    !(externalStateRule.mint && !externalStateRule.styleBlock)
  ) {
    // A rule already exists/is selected for this style block:
    return {
      styleBlockState,
      ruleWasCreated: false,
      rule: externalStateRule,
    };
  }

  // A style block doesn't currently exist, but we're trying to set some styles.
  // This means we need to auto-create a class style block and apply the styles to it.

  const {currentPageId} = designerState;
  const isBranchMode = getIsBranchMode(designerState);

  const currentComponent = selectCurrentComponent({
    DesignerStore: designerState,
    UiNodeStore: uiNodeState,
  });
  const componentMap = getLiveComponentMap(designerState);

  // Create the auto-generated class styleBlock:
  const updatedStyleBlocks = getSuggestionApplyResult({
    styleBlockState,
    suggestion: getAutoGeneratedClassSuggestion({
      selectedElement,
      currentComponent,
      componentMap,
      plugins,
      styleBlockState,
      autoCreatePreference,
    }),
    currentStyleBlockGuid: null,
    currentPageId,
    newStyleBlockId,
    isBranchMode,
    createdBy: userId,
  }).updatedStyleBlocks;

  return {
    styleBlockState: updatedStyleBlocks,
    ruleWasCreated: true,
    rule: {
      styleBlock: newStyleBlockId,
      mediaQuery: externalStateMediaQuery,
      customNodeState: externalStateCustomNodeState ?? null,
      pseudoState: externalStatePseudoMode ?? null,
      // `variantState` needs to be set to the current value of `externalStateVariantState` as
      // a new style block may be created while editing a component with a style variant active,
      // in which case any modified styles should be stored under the new `styleBlock.variants`
      // key matching the selected variant instead of at the top-level of the new style block
      variantState: externalStateVariantState,
    } as any as StyleRule,
  };
};

export const forTestSuite = {
  getAllRulesForElement,
  getIdRules,
  pluckStyleRules_old,
  pluckStyleRules_new,
  collectRules,
  getMatchingConditionalStates,
  sortStyleRules,
  getAggregatedConditionalStyles,
  collectDirectStyleBlockGuids,
  collectUpstreamStyleBlockGuids,
  createStyleRule,
  getDefaultRules,
  getPluginComponentRules,
  getRulesForElement,
  getAncestorRulesForElement,
  getStyleBlockStyleRules,
} as const;

/**
 * Returns a list of style rule tokens by selector specificity
 * @param  {String}  type A StyleBlock type property
 * @param  {String} name A StyleBlock name property
 * @param  {String} variant A StyleBlock variant property
 * @param {String} selector A StyleBlock selector property
 * @param {String[]} parentChain A list of parent StyleBlock names
 * @return {String[]} A list of tokens that describe each style block in a selector by type and name
 */
export const parseStyleRuleTokens = (
  type: string,
  name: string,
  variant: string,
  selector: string,
  parentChain: Array<string> | null
): Array<{
  type: string;
  name: string;
}> => {
  const styleRuleTokens: Array<{
    name: string;
    type: string;
  }> = [];
  const pseudoState: PseudoStateKey | null = getPseudoStateKey(
    variant ? variant.replace('main_', '') : ''
  );
  if (type === 'tag') {
    styleRuleTokens.push({
      type: 'tag',
      name: getTokenLabel({tagName: name}),
    });
  }
  if (type === 'class' && parentChain) {
    for (const parent of parentChain) {
      styleRuleTokens.push({
        type: 'class',
        name: parent,
      });
    }

    styleRuleTokens.push({
      type: 'class',
      name,
    });
  } else if (type === 'class') {
    // This branch handles class change events created before we saved the class chain in the event
    // to fix combo class capitalization.

    const classArray = selector ? selector.split('.').slice(1, -1) : [];
    for (const className of classArray) {
      styleRuleTokens.push({
        type: 'class',
        name: titleize(className),
      });
    }

    styleRuleTokens.push({
      type: 'class',
      name: getTokenLabel({className: name}),
    });
  }
  if (pseudoState) {
    styleRuleTokens.push({
      type: 'pseudo',
      name: getTokenLabel({pseudoState}),
    });
  }

  return styleRuleTokens;
};
